import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Tooltip } from 'antd'
import { IconClose } from '@edulastic/icons'
import { navy900 } from '@edulastic/colors'
import ActionHubFabIcon from './ActionHubFabIcon'
import {
  ACTION_HUB_ROOT_CLASS,
  hideChatMaintenanceIcon,
  injectActionHubOverlayHideStyles,
  openChatMaintenanceNotification,
  openSalesforceChat,
  setSalesforceChatOpen,
  setupSalesforceChatButtonHiding,
} from './actionHubHelpers'
import useActionHubOverlayHidden from './useActionHubOverlayHidden'
import {
  ActionHubFab,
  ActionHubFabIconLayer,
  ActionHubFabIconWrap,
  ActionHubMenu,
  ActionHubMenuButton,
  ActionHubMenuItem,
  ActionHubRoot,
} from './styled'

const isChatMaintenanceMode =
  process.env.REACT_APP_CHAT_MAINTENANCE_MODE === 'true'

const ActionHub = ({
  actions,
  feedbackOpen,
  onFabClick,
  onActionSelect,
  joyrideExpanded = false,
  getJoyrideElementId,
}) => {
  const [expanded, setExpanded] = useState(false)
  const rootRef = useRef(null)
  const fabRef = useRef(null)
  const menuItemRefs = useRef([])
  const {
    hidden: overlayHidden,
    openSfChat,
    closeSfChat,
  } = useActionHubOverlayHidden(feedbackOpen)

  const visibleActions = actions.filter((action) =>
    action.isVisible ? action.isVisible() : true
  )

  const isMenuExpanded = expanded || joyrideExpanded
  const canShowFabTooltip = !isMenuExpanded && !overlayHidden
  const [fabTooltipVisible, setFabTooltipVisible] = useState(false)

  useEffect(() => {
    setupSalesforceChatButtonHiding()
    hideChatMaintenanceIcon()
    injectActionHubOverlayHideStyles()
  }, [])

  useEffect(() => {
    if (overlayHidden) {
      setExpanded(false)
    }
  }, [overlayHidden])

  useEffect(() => {
    if (!canShowFabTooltip) {
      setFabTooltipVisible(false)
    }
  }, [canShowFabTooltip])

  useEffect(() => {
    if (!isMenuExpanded) return undefined

    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setExpanded(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [isMenuExpanded])

  const collapse = useCallback(() => {
    setExpanded(false)
  }, [])

  const getFocusableMenuItems = useCallback(
    () => menuItemRefs.current.filter((el) => el && !el.disabled),
    []
  )

  const focusMenuItemAt = useCallback(
    (index) => {
      const items = getFocusableMenuItems()
      if (!items.length) return

      // Defensive wrap: callers only pass non-negative indices today, but the extra
      // + length step also handles negatives correctly (JS gives -1 % 4 = -1, not 3).
      const nextIndex = ((index % items.length) + items.length) % items.length
      items[nextIndex].focus()
    },
    [getFocusableMenuItems]
  )

  const handleFabClick = () => {
    const nextExpanded = !expanded
    setExpanded(nextExpanded)
    onFabClick?.({ expanded: nextExpanded })
  }

  const handleFabKeyDown = (event) => {
    if (!isMenuExpanded) return

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const items = getFocusableMenuItems()
      if (!items.length) return
      const index = event.key === 'ArrowDown' ? 0 : items.length - 1
      focusMenuItemAt(index)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      collapse()
    }
  }

  const handleMenuKeyDown = (event, actionIndex) => {
    const items = getFocusableMenuItems()
    const currentItem = menuItemRefs.current[actionIndex]
    const currentIndex = items.indexOf(currentItem)

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (currentIndex >= items.length - 1) {
        fabRef.current?.focus()
      } else {
        focusMenuItemAt(currentIndex + 1)
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (currentIndex <= 0) {
        fabRef.current?.focus()
      } else {
        focusMenuItemAt(currentIndex - 1)
      }
    } else if (event.key === 'Escape') {
      event.preventDefault()
      collapse()
      fabRef.current?.focus()
    }
  }

  const handleActionClick = (action) => {
    collapse()
    onActionSelect?.(action)
    action.onSelect?.()
  }

  const handleSupportClick = () => {
    if (isChatMaintenanceMode) {
      openChatMaintenanceNotification()
      return
    }
    openSfChat()
    setSalesforceChatOpen(true)
    if (!openSalesforceChat()) {
      closeSfChat()
      setSalesforceChatOpen(false)
    }
  }

  return (
    <ActionHubRoot
      ref={rootRef}
      className={ACTION_HUB_ROOT_CLASS}
      data-cy="action-hub-root"
      $hidden={overlayHidden}
      aria-hidden={overlayHidden}
    >
      {visibleActions.length > 0 && (
        <ActionHubMenu
          $expanded={isMenuExpanded}
          $joyrideActive={joyrideExpanded}
          role="menu"
          aria-label="Action Hub menu"
          aria-hidden={!isMenuExpanded}
          data-joyride-id={getJoyrideElementId?.('menu')}
        >
          {visibleActions.map((action, actionIndex) => {
            const menuButton = (
              <ActionHubMenuButton
                ref={(el) => {
                  menuItemRefs.current[actionIndex] = el
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                data-cy={`action-hub-${action.id}`}
                data-joyride-id={getJoyrideElementId?.(action.id)}
                aria-label={action.label}
                disabled={action.disabled}
                $disabled={action.disabled}
                onKeyDown={(event) => handleMenuKeyDown(event, actionIndex)}
                onClick={() => {
                  if (action.disabled) return
                  if (action.id === 'support') {
                    handleSupportClick()
                  }
                  handleActionClick(action)
                }}
              >
                {action.label}
              </ActionHubMenuButton>
            )

            return (
              <ActionHubMenuItem key={action.id} role="none">
                {action.disabled && action.tooltipTitle ? (
                  <Tooltip title={action.tooltipTitle}>
                    <span>{menuButton}</span>
                  </Tooltip>
                ) : (
                  menuButton
                )}
              </ActionHubMenuItem>
            )
          })}
        </ActionHubMenu>
      )}
      <Tooltip
        title="Help and resources"
        placement="left"
        mouseEnterDelay={0.3}
        visible={canShowFabTooltip && fabTooltipVisible}
        onVisibleChange={(visible) => {
          if (canShowFabTooltip) {
            setFabTooltipVisible(visible)
          }
        }}
      >
        <ActionHubFab
          ref={fabRef}
          type="button"
          $expanded={isMenuExpanded}
          aria-label={isMenuExpanded ? 'Close Action Hub' : 'Open Action Hub'}
          aria-expanded={isMenuExpanded}
          aria-haspopup="menu"
          data-cy="action-hub-fab"
          data-joyride-id={getJoyrideElementId?.('fab')}
          onClick={handleFabClick}
          onKeyDown={handleFabKeyDown}
        >
          <ActionHubFabIconWrap>
            <ActionHubFabIconLayer
              $visible={!isMenuExpanded}
              aria-hidden={isMenuExpanded}
            >
              <ActionHubFabIcon width={56} height={56} color="white" />
            </ActionHubFabIconLayer>
            <ActionHubFabIconLayer
              $visible={isMenuExpanded}
              aria-hidden={!isMenuExpanded}
            >
              <IconClose width={14} height={14} color={navy900} />
            </ActionHubFabIconLayer>
          </ActionHubFabIconWrap>
        </ActionHubFab>
      </Tooltip>
    </ActionHubRoot>
  )
}

export default ActionHub
